import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  FiArrowLeft,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiCalendar,
  FiUser,
  FiInfo,
  FiEdit,
  FiTrash2,
  FiCopy,
  FiSave,
  FiMonitor,
  FiShield,
  FiLink2,
  FiUsers,
  FiRadio
} from 'react-icons/fi';
import { getLicenseById, updateLicense, deleteLicense, unbindLicense } from '../../services/licenseService';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';

const LicenseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  
  // 状态
  const [isEditing, setIsEditing] = useState(false);
  const [status, setStatus] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [remark, setRemark] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUnbindModal, setShowUnbindModal] = useState(false);
  // 云端跟单配置(独立分区, 独立保存, 改动即时生效)
  const [followEnabled, setFollowEnabled] = useState(false);
  const [followGroup, setFollowGroup] = useState('');
  const [followExpiresAt, setFollowExpiresAt] = useState('');
  
  // 获取授权码详情
  const { data, isLoading, error } = useQuery(
    ['license', id],
    () => getLicenseById(id),
    {
      onSuccess: (data) => {
        setStatus(data.license.status);
        setExpiresAt(data.license.expiresAt ? new Date(data.license.expiresAt).toISOString().split('T')[0] : '');
        setCustomerName(data.license.customerName || '');
        setRemark(data.license.remark || '');
        setFollowEnabled(!!data.license.followEnabled);
        setFollowGroup(data.license.followGroup || '');
        setFollowExpiresAt(data.license.followExpiresAt ? new Date(data.license.followExpiresAt).toISOString().split('T')[0] : '');
      }
    }
  );
  
  // 更新授权码
  const updateMutation = useMutation(
    (updateData) => updateLicense(id, updateData),
    {
      onSuccess: () => {
        toast.success('授权码更新成功');
        setIsEditing(false);
        queryClient.invalidateQueries(['license', id]);
      },
      onError: (error) => {
        toast.error(error.message || '更新授权码失败');
      }
    }
  );
  
  // 删除授权码
  const deleteMutation = useMutation(
    () => deleteLicense(id),
    {
      onSuccess: () => {
        toast.success('授权码删除成功');
        navigate('/licenses');
      },
      onError: (error) => {
        toast.error(error.message || '删除授权码失败');
      }
    }
  );
  
  // 解绑设备
  const unbindMutation = useMutation(
    () => unbindLicense(id),
    {
      onSuccess: () => {
        toast.success('设备绑定已清除，该授权码可重新绑定新设备');
        setShowUnbindModal(false);
        queryClient.invalidateQueries(['license', id]);
      },
      onError: (error) => {
        toast.error(error.message || '解绑设备失败');
      }
    }
  );

  // 保存云端跟单配置(独立保存, 即时生效)
  const followMutation = useMutation(
    () => updateLicense(id, {
      followEnabled,
      followGroup: followGroup.trim(),
      followExpiresAt: followExpiresAt || null
    }),
    {
      onSuccess: () => {
        toast.success(followEnabled ? '跟单配置已保存, 即时生效' : '跟单已关闭, 即时生效');
        queryClient.invalidateQueries(['license', id]);
      },
      onError: (error) => {
        toast.error(error.message || '保存跟单配置失败');
      }
    }
  );

  // 处理保存编辑
  const handleSaveEdit = () => {
    const updateData = {
      status,
      expiresAt: expiresAt || null,
      customerName: customerName.trim() || '',
      remark: remark.trim() || ''
    };
    
    updateMutation.mutate(updateData);
  };
  
  // 处理删除
  const handleDelete = () => {
    deleteMutation.mutate();
  };
  
  const copytxt = (text) => {
    // 创建临时文本区域
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    
    // 选择文本区域中的文本
    textarea.select();
    textarea.setSelectionRange(0, 99999); // 对于手机，选择范围
    
    // 执行复制命令
    document.execCommand('copy');
    
    // 移除临时文本区域
    document.body.removeChild(textarea);
};

  // 复制授权码
  const copyToClipboard = (text) => {
    copytxt(text);
    toast.success('授权码已复制到剪贴板');
  };
  
  // 获取状态标签
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-active badge-active-dot"><FiCheckCircle className="mr-1" />已激活</span>;
      case 'pending':
        return <span className="badge badge-pending badge-pending-dot"><FiClock className="mr-1" />待授权</span>;
      case 'expired':
        return <span className="badge badge-expired"><FiXCircle className="mr-1" />已过期</span>;
      case 'revoked':
        return <span className="badge badge-revoked"><FiXCircle className="mr-1" />已撤销</span>;
      default:
        return <span className="badge badge-revoked">未知</span>;
    }
  };
  
  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '无';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN') + ' ' + date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  };
  
  // 页面动画
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3
      }
    }
  };
  
  // 加载状态
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="skeleton h-8 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 skeleton h-96" />
          <div className="skeleton h-64" />
        </div>
      </div>
    );
  }
  
  // 错误状态
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="text-red-500 text-xl mb-4">获取授权码详情失败</div>
        <Button onClick={() => navigate('/licenses')}>返回列表</Button>
      </div>
    );
  }
  
  const license = data?.license;
  
  if (!license) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="text-yellow-500 text-xl mb-4">授权码不存在</div>
        <Button onClick={() => navigate('/licenses')}>返回列表</Button>
      </div>
    );
  }
  
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full"
    >
      {/* 页面标题 */}
      <motion.div variants={itemVariants} className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/licenses')}
            variant="ghost"
            icon={<FiArrowLeft />}
            className="p-2"
          />
          <div>
            <h1 className="text-2xl font-bold text-[#1f2328]">授权码详情</h1>
            <p className="text-[#656d76] mt-1">查看和管理授权码信息</p>
          </div>
        </div>
        
        <div className="flex gap-3">
          {!isEditing ? (
            <>
              <Button
                onClick={() => setIsEditing(true)}
                variant="secondary"
                icon={<FiEdit />}
              >
                编辑
              </Button>
              <Button
                onClick={() => setShowDeleteModal(true)}
                variant="danger"
                icon={<FiTrash2 />}
              >
                删除
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => setIsEditing(false)}
                variant="secondary"
              >
                取消
              </Button>
              <Button
                onClick={handleSaveEdit}
                variant="primary"
                icon={<FiSave />}
                disabled={updateMutation.isLoading}
              >
                {updateMutation.isLoading ? '保存中...' : '保存'}
              </Button>
            </>
          )}
        </div>
      </motion.div>
      
      {/* 授权码详情 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 主要信息 */}
        <motion.div
          variants={itemVariants}
          className="lg:col-span-2 bg-white rounded-md shadow-sm p-6"
        >
          <h2 className="text-lg font-semibold text-[#1f2328] mb-6 flex items-center">
            <FiInfo className="mr-2" /> 基本信息
          </h2>
          
          {/* 授权码 */}
          <div className="mb-6">
            <label className="block text-[#656d76] text-sm mb-2">授权码</label>
            <div className="flex items-center">
              <div className="bg-[#f6f8fa] rounded-md py-3 px-4 font-mono text-[#1f2328] flex-1 break-all">
                {license.licenseKey}
              </div>
              <Button
                onClick={() => copyToClipboard(license.licenseKey)}
                variant="secondary"
                icon={<FiCopy />}
                className="ml-2 px-3"
              >
                复制
              </Button>
            </div>
          </div>
          
          {/* 创建者(超管查看他人授权码时显示) */}
          {license.creator && (
            <div className="mb-6">
              <label className="block text-[#656d76] text-sm mb-2">创建者</label>
              <div className="py-2 text-[#1f2328] flex items-center">
                <FiUser className="mr-2 text-[#656d76]" />
                {license.creator.username}
                {license.creator.id === currentUser?.id && <span className="ml-2 text-xs brand-text">(我)</span>}
              </div>
            </div>
          )}
          
          {/* 客户名称 */}
          <div className="mb-6">
            <label className="block text-[#656d76] text-sm mb-2">客户名称</label>
            {isEditing ? (
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                maxLength={100}
                placeholder="例如: 张三 / 微信ID / 订单号"
                className="w-full bg-[#f6f8fa] rounded-md py-3 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none input-primary"
              />
            ) : (
              <div className="py-2 text-[#1f2328]">
                {license.customerName || <span className="text-[#8b949e]">未填写</span>}
              </div>
            )}
          </div>

          {/* MT5账户(EA上报自动回写, 不可编辑) */}
          <div className="mb-6">
            <label className="block text-[#656d76] text-sm mb-2">MT5账户</label>
            <div className="py-2 text-[#1f2328] flex items-center">
              {license.account ? (
                <>
                  <span className="font-mono mr-2">{license.account}</span>
                  <span className="text-xs text-[#8b949e]">(EA连接后自动记录)</span>
              </>
              ) : (
                <span className="text-[#8b949e]">EA未连接</span>
              )}
            </div>
          </div>
          
          {/* 备注 */}
          <div className="mb-6">
            <label className="block text-[#656d76] text-sm mb-2">备注</label>
            {isEditing ? (
              <textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="联系方式/购买渠道/到期处理等"
                className="w-full bg-[#f6f8fa] rounded-md py-3 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none input-primary resize-y"
              />
            ) : (
              <div className="py-2 text-[#1f2328] whitespace-pre-wrap break-all">
                {license.remark || <span className="text-[#8b949e]">未填写</span>}
              </div>
            )}
          </div>
          
          {/* 状态 */}
          <div className="mb-6">
            <label className="block text-[#656d76] text-sm mb-2">状态</label>
            {isEditing ? (
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full bg-[#f6f8fa] rounded-md py-3 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none input-primary"
              >
                <option value="pending">待授权</option>
                <option value="active">已激活</option>
                <option value="expired">已过期</option>
                <option value="revoked">已撤销</option>
              </select>
            ) : (
              <div className="py-2">
                {getStatusBadge(license.status)}
              </div>
            )}
          </div>
          
          {/* 过期时间 */}
          <div className="mb-6">
            <label className="block text-[#656d76] text-sm mb-2">过期时间</label>
            {isEditing ? (
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full bg-[#f6f8fa] rounded-md py-3 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none input-primary"
              />
            ) : (
              <div className="py-2 text-[#1f2328] flex items-center">
                <FiCalendar className="mr-2" />
                {license.expiresAt ? formatDate(license.expiresAt) : '永久有效'}
              </div>
            )}
          </div>
          
          {/* 时间信息 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-[#656d76] text-sm mb-2">创建时间</label>
              <div className="py-2 text-[#1f2328] flex items-center">
                <FiCalendar className="mr-2" />
                {formatDate(license.createdAt)}
              </div>
            </div>
            <div>
              <label className="block text-[#656d76] text-sm mb-2">激活时间</label>
              <div className="py-2 text-[#1f2328] flex items-center">
                {license.activatedAt ? (
                  <>
                    <FiCalendar className="mr-2" />
                    {formatDate(license.activatedAt)}
                  </>
                ) : (
                  <span className="text-[#8b949e]">未激活</span>
                )}
              </div>
            </div>
          </div>

          {/* 设备绑定信息（MQL5 联网授权配套） */}
          <div className="mt-8 border-t border-[#d0d7de] pt-6">
            <h3 className="text-lg font-semibold text-[#1f2328] mb-4 flex items-center">
              <FiMonitor className="mr-2 brand-text" /> 设备绑定
            </h3>
            {(() => {
              let devices = [];
              try {
                devices = license.deviceId ? JSON.parse(license.deviceId) : [];
              } catch (e) {
                devices = [];
              }
              const maxDevices = license.maxDevices || 1;
              const boundCount = Array.isArray(devices) ? devices.length : 0;
              return (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="bg-[#f6f8fa] rounded-md px-3 py-2 flex items-center gap-2">
                      <span className="text-[#656d76]">在线状态</span>
                      {license.isOnline ? (
                        <span className="badge badge-online badge-online-dot">在线</span>
                      ) : (
                        <span className="badge badge-offline badge-offline-dot">离线</span>
                      )}
                    </div>
                    <div className="bg-[#f6f8fa] rounded-md px-3 py-2">
                      <span className="text-[#656d76] mr-2">已绑定设备</span>
                      <span className="text-[#1f2328] font-semibold">{boundCount} / {maxDevices}</span>
                    </div>
                    {license.lastVerifiedAt && (
                      <div className="bg-[#f6f8fa] rounded-md px-3 py-2">
                        <span className="text-[#656d76] mr-2">最近验证</span>
                        <span className="text-[#1f2328]">{formatDate(license.lastVerifiedAt)}</span>
                      </div>
                    )}
                  </div>

                  {boundCount > 0 ? (
                    <div className="space-y-2">
                      {devices.map((dev, i) => (
                        <div key={i} className="flex items-center gap-2 bg-[#f6f8fa] rounded-md px-3 py-2">
                          <FiShield className="brand-text shrink-0" />
                          <span className="font-mono text-sm text-[#24292f] truncate">{dev}</span>
                          {i === 0 && license.deviceName && (
                            <span className="ml-auto text-xs text-[#656d76] shrink-0 truncate max-w-[10rem]">
                              {license.deviceName}
                            </span>
                          )}
                        </div>
                      ))}
                      {/* 换机入口: 清除绑定后客户端在新设备首次验证时自动绑定 */}
                      <div className="pt-1">
                        <Button
                          onClick={() => setShowUnbindModal(true)}
                          variant="secondary"
                          icon={<FiLink2 />}
                          size="sm"
                        >
                          解绑设备
                        </Button>
                        <p className="text-[#8b949e] text-xs mt-1.5">更换电脑/VPS 时先解绑，再在新设备挂上 EA 即可自动绑定</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-6 text-[#8b949e] flex-col">
                      <FiLink2 className="text-3xl mb-2 opacity-50" />
                      <p className="text-sm">尚未绑定设备。授权码激活后，客户端首次联网验证将自动绑定设备。</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* 云端跟单配置(独立分区, 独立保存, 改动下一轮poll即时生效) */}
          <div className="mt-8 border-t border-[#d0d7de] pt-6">
            <h3 className="text-lg font-semibold text-[#1f2328] mb-4 flex items-center">
              <FiRadio className="mr-2 brand-text" /> 云端跟单
            </h3>

            <div className="space-y-4">
              {/* 跟单开关 */}
              <div className="flex items-center justify-between bg-[#f6f8fa] rounded-md px-4 py-3">
                <div>
                  <div className="text-sm text-[#1f2328] font-medium">跟单总开关</div>
                  <div className="text-xs text-[#8b949e] mt-0.5">关闭后该码不再收到云端开仓信号(约0.5秒生效); 全部平仓等风控指令不受影响</div>
                </div>
                <button
                  onClick={() => setFollowEnabled(!followEnabled)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                    followEnabled ? 'bg-[#1a7f37]' : 'bg-[#d0d7de]'
                  }`}
                  title={followEnabled ? '点击关闭' : '点击开启'}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    followEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {/* 跟单分组 */}
              <div>
                <label className="block text-sm text-[#1f2328] mb-1.5">
                  跟单分组 <span className="text-[#8b949e] text-xs">(策略源按分组定向分发, 留空=仅全发策略覆盖)</span>
                </label>
                <input
                  type="text"
                  value={followGroup}
                  onChange={(e) => setFollowGroup(e.target.value)}
                  maxLength={50}
                  placeholder="如: VIP / 批发 / 试用"
                  className="w-full bg-[#f6f8fa] rounded-md py-2.5 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none input-primary text-sm"
                />
              </div>

              {/* 跟单到期日 */}
              <div>
                <label className="block text-sm text-[#1f2328] mb-1.5">
                  跟单到期日 <span className="text-[#8b949e] text-xs">(留空=永久; 到期后自动停止接收开仓信号)</span>
                </label>
                <input
                  type="date"
                  value={followExpiresAt}
                  onChange={(e) => setFollowExpiresAt(e.target.value)}
                  className="w-full md:w-64 bg-[#f6f8fa] rounded-md py-2.5 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none input-primary text-sm"
                />
              </div>

              {/* 水印 magic */}
              <div className="flex items-center gap-2 text-xs text-[#57606a] bg-[#f6f8fa] rounded-md px-4 py-2.5">
                <FiShield className="brand-text shrink-0" />
                <span>跟单水印 magic</span>
                <span className="font-mono text-[#1f2328]">{license.magic || '首次跟单时自动分配'}</span>
                <span className="text-[#8b949e] hidden md:inline">(信号外泄时凭持仓单 magic 溯源到此授权码)</span>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={() => followMutation.mutate()}
                  variant="primary"
                  icon={<FiSave />}
                  disabled={followMutation.isLoading}
                  size="sm"
                >
                  {followMutation.isLoading ? '保存中...' : '保存跟单配置'}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* 用户信息 */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-md shadow-sm p-6"
        >
          <h2 className="text-lg font-semibold text-[#1f2328] mb-6 flex items-center">
            <FiUser className="mr-2" /> 用户信息
          </h2>
          
          {license.userInfo ? (
            <div className="space-y-4">
              {Object.entries(license.userInfo).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-[#656d76] text-sm mb-1 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </label>
                  <div className="bg-[#f6f8fa] rounded-md py-2 px-3 text-[#1f2328]">
                    {typeof value === 'object' ? JSON.stringify(value) : value.toString()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-[#8b949e]">
              <FiInfo className="text-4xl mb-3" />
              <p>暂无用户信息</p>
            </div>
          )}
        </motion.div>
      </div>
      
      {/* 删除确认弹窗 */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="删除授权码"
      >        <div className="mb-6">
          <p className="text-[#57606a]">确定要删除这个授权码吗？此操作不可逆。</p>
          <div className="mt-4 bg-[#f6f8fa] p-3 rounded-md">
            <div className="font-mono text-[#1f2328]">{license.licenseKey}</div>
          </div>
        </div>
        <div className="flex justify-end gap-4">
          <Button
            onClick={() => setShowDeleteModal(false)}
            variant="secondary"
          >
            取消
          </Button>
          <Button
            onClick={handleDelete}
            variant="danger"
            icon={<FiTrash2 />}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? '删除中...' : '删除'}
          </Button>
        </div>
      </Modal>

      {/* 解绑设备确认弹窗 */}
      <Modal
        isOpen={showUnbindModal}
        onClose={() => setShowUnbindModal(false)}
        title="解绑设备"
      >
        <div className="mb-6">
          <p className="text-[#57606a]">确定要清除该授权码的设备绑定吗？</p>
          <p className="text-[#57606a] mt-2">解绑后绑定记录清空，<span className="text-[#cf222e]">下一个完成验证的设备会自动成为绑定设备</span>。换机请按步骤：先停掉旧设备上的 EA → 在此解绑 → 再在新设备挂上 EA；若旧设备仍在运行，它会重新占用绑定名额。</p>
          <div className="mt-4 bg-[#f6f8fa] p-3 rounded-md">
            <div className="font-mono text-[#1f2328]">{license.licenseKey}</div>
          </div>
        </div>
        <div className="flex justify-end gap-4">
          <Button
            onClick={() => setShowUnbindModal(false)}
            variant="secondary"
          >
            取消
          </Button>
          <Button
            onClick={() => unbindMutation.mutate()}
            variant="danger"
            icon={<FiLink2 />}
            disabled={unbindMutation.isLoading}
          >
            {unbindMutation.isLoading ? '解绑中...' : '确认解绑'}
          </Button>
        </div>
      </Modal>
    </motion.div>
  );
};

export default LicenseDetail;